import React from "react";

// FDI notation (dot format): quadrants 1..4, tooth 1..8
// Q1: upper right, Q2: upper left, Q3: lower left, Q4: lower right
// Visual layout (patient-facing): show upper row (Q1 reversed + Q2) then lower (Q4 reversed + Q3).

const upperRow = [
  ..."87654321".split("").map((n) => `1.${n}`), // Q1 reversed → 1.8 … 1.1
  ..."12345678".split("").map((n) => `2.${n}`), // Q2 → 2.1 … 2.8
];
const lowerRow = [
  ..."87654321".split("").map((n) => `4.${n}`), // Q4 reversed
  ..."12345678".split("").map((n) => `3.${n}`), // Q3
];

export const CONDITIONS = {
  healthy:   { color: "#F4F4F5", border: "#D4D4D8", text: "#1C1C1C", label: "Healthy" },
  caries:    { color: "#D93838", border: "#991B1B", text: "#fff", label: "Caries" },
  filled:    { color: "#1E3A8A", border: "#1E3A8A", text: "#fff", label: "Filled" },
  missing:   { color: "transparent", border: "#A1A1AA", text: "#A1A1AA", dashed: true, label: "Missing" },
  crown:     { color: "#D69E2E", border: "#B7791F", text: "#fff", label: "Crown" },
  rct:       { color: "#4338CA", border: "#3730A3", text: "#fff", label: "RCT" },
  planned:   { color: "#F6AD55", border: "#DD6B20", text: "#7B341E", label: "Planned" },
  completed: { color: "#15803D", border: "#166534", text: "#fff", label: "Completed" },
};

function Tooth({ code, cond, selected, onClick }) {
  const c = CONDITIONS[cond || "healthy"];
  return (
    <button
      type="button"
      data-testid={`tooth-${code}`}
      onClick={() => onClick(code)}
      className={`tooth ${selected ? "selected" : ""} flex flex-col items-center justify-center`}
      style={{
        width: 34, height: 44,
        background: c.color,
        border: `${c.dashed ? "2px dashed" : "1.5px solid"} ${c.border}`,
        borderRadius: 6,
        color: c.text,
      }}
      title={`${code} · ${c.label}`}
    >
      <span className="text-[10px] font-mono-num font-semibold leading-none">{code}</span>
    </button>
  );
}

export default function ToothChart({ findings, selected, onSelect }) {
  const map = new Map((findings || []).map((f) => [f.tooth, f.condition]));
  const isSelected = (t) => selected?.includes(t);

  return (
    <div className="inline-block">
      {/* upper */}
      <div className="flex items-center gap-1 mb-2">
        <div className="text-[10px] uppercase tracking-widest text-[#52525B] w-14">Upper</div>
        {upperRow.map((t, i) => (
          <React.Fragment key={t}>
            {i === 8 && <div className="w-1.5 h-8 border-l border-dashed border-[#A1A1AA] mx-1" />}
            <Tooth code={t} cond={map.get(t)} selected={isSelected(t)} onClick={onSelect} />
          </React.Fragment>
        ))}
      </div>
      {/* lower */}
      <div className="flex items-center gap-1">
        <div className="text-[10px] uppercase tracking-widest text-[#52525B] w-14">Lower</div>
        {lowerRow.map((t, i) => (
          <React.Fragment key={t}>
            {i === 8 && <div className="w-1.5 h-8 border-l border-dashed border-[#A1A1AA] mx-1" />}
            <Tooth code={t} cond={map.get(t)} selected={isSelected(t)} onClick={onSelect} />
          </React.Fragment>
        ))}
      </div>
      {/* legend */}
      <div className="mt-4 flex flex-wrap gap-2">
        {Object.entries(CONDITIONS).map(([k, c]) => (
          <div key={k} className="flex items-center gap-1.5 text-xs text-[#52525B]" data-testid={`legend-${k}`}>
            <span
              style={{
                width: 12, height: 12, borderRadius: 3, background: c.color,
                border: `${c.dashed ? "1.5px dashed" : "1px solid"} ${c.border}`,
              }}
            />
            {c.label}
          </div>
        ))}
      </div>
    </div>
  );
}
